import type { PageHelp } from "../../help/types";

export const adminPageHelp: PageHelp = {
  title: "Admin",
  intro:
    "The global benefit catalog, where Global admins define the benefit categories: name, consent and auto-enrollment defaults, cover picture, description and documents. Country admins then add these categories to their countries on the Country page, and the Analytics tab opens the Power BI report.",
  sections: [
    "Getting around",
    "Toolbar",
    "Reading the cards",
    "List view",
    "Category dialog",
    "Image library",
  ],
  contexts: {
    "card-view":
      'Shown in the card view, which is the default. If you see a table instead, open the settings menu in the header and set View Mode to "Grid View".',
    "list-view":
      'Open the settings menu in the header and set View Mode to "List View".',
    "category-dialog":
      'Click "Add Benefit Category" in the toolbar, or the Edit icon on a category card.',
    "add-dialog": 'Click "Add Benefit Category" in the toolbar.',
    "edit-dialog":
      "Click the Edit icon on a category card, or the Edit icon in the Actions column of the list view.",
    "images-dialog": 'Click "Manage Images" in the toolbar (Superadmin only).',
    "image-picker":
      'Click the camera icon on a category card, or "Select Image" next to the cover picture in the category dialog (Superadmin only).',
  },
  tips: [
    "Countries get their own copy of a category on the Country page. A copy keeps the name but has its own consent, auto-enrollment, cover picture and description, and later edits here do not change copies that already exist.",
    "Check the name before you save a new category. The name of an existing category cannot be changed on this page, because the server rejects a save with a different name.",
    "View Mode (List View or Grid View) in the header settings menu is saved to your user settings and also applies to the other pages that list benefits.",
    "Only Superadmins see the image library and the cover-picture controls. Other admins see cover pictures read-only.",
    "With a read-only Global admin role you can browse, search and open the analytics, but the add, edit and delete controls are hidden or greyed out.",
  ],
  steps: [
    // Getting around
    {
      selector: '[data-help="admin-tab-benefits"]',
      section: "Getting around",
      title: "Work with the benefit catalog",
      body: "Shows every global benefit category, as cards or as a table depending on your View Mode setting. This tab is selected when you open the page.",
    },
    {
      selector: '[data-help="admin-tab-analytics"]',
      section: "Getting around",
      title: "Open the analytics report",
      body: "Replaces the catalog with the embedded Power BI analytics report. Move between report pages with the navigation inside the report. The refresh button next to the search box reloads the report.",
    },
    {
      selector: '[data-help="subheader-refresh"]',
      section: "Getting around",
      title: "Reload the data",
      body: "On the Benefits tab, fetches the categories again from the server, for example after a colleague has changed the catalog. On the Analytics tab, reloads the report instead.",
    },
    {
      selector: '[data-help="subheader-search"]',
      section: "Getting around",
      title: "Search categories by name",
      body: "Filters the cards or the table to categories whose name contains the text you type. Upper and lower case do not matter. The count at the right end of the bar shows how many categories match. Save Order is greyed out while a search is active.",
    },

    // Toolbar
    {
      selector: '[data-help="benefit-cards-save-order"]',
      section: "Toolbar",
      context: "card-view",
      title: "Save your card order",
      body: "Drag the cards into the order you want, then click Save Order to keep it. The order is saved to your own user settings, so other users still see their own order. Greyed out while a search is active.",
    },
    {
      selector: '[data-help="admin-manage-images"]',
      section: "Toolbar",
      minRole: "superadmin",
      title: "Manage the image library",
      body: "Opens the shared library of pictures that can be used as cover pictures. In the library you can upload new images and delete images that are not in use.",
    },
    {
      selector: '[data-help="admin-add-category"]',
      section: "Toolbar",
      title: "Add a benefit category",
      body: 'Opens the "Add Benefit Category" dialog, where you create a new global category. Read-only Global admins see this button greyed out. After you save, country admins can add the category to their countries on the Country page.',
    },

    // Reading the cards
    {
      selector: '[data-help="benefit-card"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      title: "Read a category card",
      body: "Each card shows the category's cover picture, name and description. Hover over the name or the description to read the full text. To change the order, drag a card to a new position, then click Save Order.",
    },
    {
      selector: '[data-help="benefit-card-edit"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "admin",
      title: "Edit a category",
      body: 'Opens the "Edit Category" dialog with the current values of the category. You cannot change the name here, because the server rejects a save with a different name.',
    },
    {
      selector: '[data-help="benefit-card-documents"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "admin",
      title: "Manage the category's documents",
      body: 'Opens "Manage Documents", where you upload files to the category and edit or delete its documents. Document changes are saved in that dialog, separately from the category itself.',
    },
    {
      selector: '[data-help="benefit-card-photo"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "superadmin",
      title: "Change the cover picture",
      body: "Opens the image library in picking mode. Click the camera button next to an image to make it the cover picture. The category is saved immediately, without a separate Save.",
    },
    {
      selector: '[data-help="benefit-card-delete"]',
      section: "Reading the cards",
      context: "card-view",
      optional: true,
      minRole: "superadmin",
      title: "Delete a category",
      body: "Asks for confirmation, then deletes the category and its documents. The delete also removes every country and client category based on it, and the client benefits built on those. The server accepts a category delete only from members of the Global admins group.",
    },

    // List view
    {
      selector: '[data-help="benefit-category-column-cover"]',
      section: "List view",
      context: "list-view",
      title: "Check the cover picture",
      body: "Shows a thumbnail of the cover picture. Hover over it for a larger preview. Superadmins also get a camera button to pick another image and a clear button to remove it. The change stays on the row until you click Save in the Actions column.",
    },
    {
      selector: '[data-help="benefit-category-column-name"]',
      section: "List view",
      context: "list-view",
      title: "Check the category name",
      body: "Admins see the name as a text field. But the server rejects a new name for an existing catalog category, so a changed name cannot be saved. Set the name carefully when you create the category.",
    },
    {
      selector: '[data-help="benefit-category-column-documents"]',
      section: "List view",
      context: "list-view",
      title: "Open a category's documents",
      body: 'The button in each row shows how many documents the category has ("No data" when there are none). Click it to open "Manage Documents" for that category. Read-only Global admins see the button greyed out.',
    },
    {
      selector: '[data-help="benefit-category-column-consent"]',
      section: "List view",
      context: "list-view",
      title: "Set whether consent is needed",
      body: "Choose Yes or No. New benefits created under this category start with this value. Consent Needed and Is Auto Enrollment cannot both be Yes: the table does not prevent it, but the server rejects such a row when you click Save.",
    },
    {
      selector: '[data-help="benefit-category-column-auto-enrollment"]',
      section: "List view",
      context: "list-view",
      title: "Set auto-enrollment",
      body: "Choose Yes or No. New benefits created under this category start with this value. Set Consent Needed to No first, because the server rejects a row with both set to Yes.",
    },
    {
      selector: '[data-help="benefit-category-column-description"]',
      section: "List view",
      context: "list-view",
      title: "Edit the description in the table",
      body: "Type in the cell to change the description. The row is marked as changed and is sent to the server only when you click Save in the Actions column. The description is required, so the server rejects an empty description.",
    },
    {
      selector: '[data-help="benefit-category-column-actions"]',
      section: "List view",
      context: "list-view",
      title: "Save, undo or delete a row",
      body: "Edit opens the category dialog, and the bin deletes the category after confirmation. After you change a cell, Save and Undo appear: Save sends that row to the server, and Undo restores the last saved values. Read-only Global admins see these buttons greyed out.",
    },

    // Category dialog
    {
      selector: '[data-help="benefit-category-dialog-name"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Name the category",
      body: "Required, 2 to 128 characters, and unique among the global categories. You cannot rename the category after it is created, because the server rejects a save with a different name.",
    },
    {
      selector: '[data-help="benefit-category-dialog-consent"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Set whether consent is needed",
      body: "New benefits created under this category start with this value. If you choose Yes, Is Auto Enrollment is set to No and greyed out, because the two cannot both be Yes.",
    },
    {
      selector: '[data-help="benefit-category-dialog-auto-enrollment"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Set auto-enrollment",
      body: "New benefits created under this category start with this value. If you choose Yes, Consent Needed is set to No. This option is greyed out while Consent Needed is Yes.",
    },
    {
      selector: '[data-help="benefit-category-dialog-cover"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Choose a cover picture",
      body: 'Superadmins see "Choose Cover Picture" with a "Select Image" button, which picks an image from the shared library, and an x button that removes the current picture. Other admins see the current picture read-only. The picture is saved when you click Save.',
    },
    {
      selector: '[data-help="benefit-category-dialog-description"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Describe the category",
      body: "Required, 2 to 4000 characters. The text appears on the category card and in the Description column of the list view.",
    },
    {
      selector: '[data-help="benefit-category-dialog-attachments"]',
      section: "Category dialog",
      context: "add-dialog",
      minRole: "admin",
      title: "Attach documents to a new category",
      body: 'Under "Document List", click Upload to pick one or more files. The files are listed below, and the bin icon removes a file from the list. Nothing is uploaded until you click Save, which uploads the files together with the new category.',
    },
    {
      selector: '[data-help="benefit-category-dialog-documents"]',
      section: "Category dialog",
      context: "edit-dialog",
      minRole: "admin",
      title: "Manage documents from the dialog",
      body: 'Opens "Manage Documents" for the category you are editing. Document changes are saved in that dialog, separately from the Save button of the category dialog. The button shows how many documents the category has.',
    },
    {
      selector: '[data-help="benefit-category-dialog-save"]',
      section: "Category dialog",
      context: "category-dialog",
      minRole: "admin",
      title: "Save the category",
      body: "Creates or updates the category and closes the dialog. The button is greyed out while a field has a validation error, for example an empty name or description.",
    },

    // Image library
    {
      selector: '[data-help="admin-images-search"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      title: "Find an image by file name",
      body: "Filters the library to images whose file name contains the text you type. Upper and lower case do not matter.",
    },
    {
      selector: '[data-help="admin-images-view"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      title: "Change how images are listed",
      body: "Choose List, Details (adds the file type), Tiles, or Large, Medium or Small Images to change the layout and the thumbnail size.",
    },
    {
      selector: '[data-help="admin-images-upload"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      title: "Upload images",
      body: "Pick one or more files from your computer. The files are added to a queue below and are not uploaded yet. A file whose name already exists in the library is skipped.",
    },
    {
      selector: '[data-help="admin-images-queued-save"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      optional: true,
      title: "Upload one queued file",
      body: "The save icon next to a queued file uploads only that file. The bin icon next to it removes the file from the queue without uploading it.",
    },
    {
      selector: '[data-help="admin-images-save-all"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      optional: true,
      title: "Upload all queued files",
      body: "Appears when more than one file is in the queue. Uploads all queued files at once and empties the queue when the uploads are complete.",
    },
    {
      selector: '[data-help="admin-images-clear"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      optional: true,
      title: "Empty the upload queue",
      body: "Removes every queued file without uploading anything. Images that are already in the library are not affected.",
    },
    {
      selector: '[data-help="admin-images-preview"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      optional: true,
      title: "Preview an image",
      body: "Hover over a thumbnail to see a larger preview. Click the preview to open the image at full size in a new browser tab.",
    },
    {
      selector: '[data-help="admin-images-delete"]',
      section: "Image library",
      context: "images-dialog",
      minRole: "superadmin",
      optional: true,
      title: "Delete an image",
      body: "Asks for confirmation, then removes the image from the library. The server does not delete an image that is still the cover picture of a category, benefit or package.",
    },
    {
      selector: '[data-help="admin-images-select"]',
      section: "Image library",
      context: "image-picker",
      minRole: "superadmin",
      optional: true,
      title: "Pick an image",
      body: "In picking mode, each image has a camera button instead of the bin. Click it to use that image, and the library closes. From a card, the category is saved immediately. From the dialog, the picture is saved when you click Save.",
    },
  ],
};
